import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { OrgResponse, PlatformOrgCreate } from "@bw/shared/types";
import { formatDate } from "@bw/shared/utils";

import { PlatformPage } from "@/components/PlatformPage";
import { orgBusinessName, orgLegalName } from "@/shared/orgDisplay";

import { createOrg, listOnboardingOrgs } from "../api";

const QUEUES = [
  { key: "pending", label: "Pending", accent: "warning.main" },
  { key: "in_review", label: "In Review", accent: "info.main" },
  { key: "approved", label: "Approved", accent: "success.main" },
] as const;

type ReadinessColor = "info" | "success" | "warning";

function readiness(org: OrgResponse): {
  color: ReadinessColor;
  label: string;
} {
  const blockerCount = org.approval_blockers?.length ?? 0;
  if (org.status === "approved" || org.status === "active") {
    return { color: "success", label: "Approved" };
  }
  if (blockerCount === 0) {
    return { color: "info", label: "Ready for review" };
  }
  return {
    color: "warning",
    label: `${blockerCount} blocker${blockerCount === 1 ? "" : "s"}`,
  };
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography color="text.secondary" variant="overline">
          {label}
        </Typography>
        <Typography component="p" variant="h5" sx={{ fontWeight: 700 }}>
          {value.toLocaleString()}
        </Typography>
      </CardContent>
    </Card>
  );
}

function OrgCard({ org, onOpen }: { org: OrgResponse; onOpen: () => void }) {
  const blockers = org.approval_blockers ?? [];
  const businessName = orgBusinessName(org);
  const readinessState = readiness(org);
  const ownerName =
    org.owner_display_name?.trim() ||
    org.owner_full_name?.trim() ||
    "Owner unavailable";

  return (
    <Card variant="outlined">
      <CardActionArea
        aria-label={`Open ${businessName}`}
        onClick={onOpen}
        sx={{ p: 2, textAlign: "left" }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { sm: "flex-start" },
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ alignItems: "center", flexWrap: "wrap" }}
              >
                <Typography
                  component="h4"
                  variant="subtitle1"
                  sx={{ fontWeight: 700 }}
                >
                  {businessName}
                </Typography>
                <Chip
                  color={readinessState.color}
                  label={readinessState.label}
                  size="small"
                  variant="outlined"
                />
              </Stack>
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{ mt: 0.5 }}
              >
                {orgLegalName(org) ?? "Legal name not provided"}
              </Typography>
            </Box>
            <Typography
              color="text.secondary"
              variant="caption"
              sx={{ flexShrink: 0 }}
            >
              {formatDate(org.created_at)}
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            <Box>
              <Typography color="text.secondary" variant="overline">
                Owner
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {ownerName}
              </Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" variant="overline">
                Stripe
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {org.stripe_onboarding_complete ? "Complete" : "Not finished"}
              </Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" variant="overline">
                Review blockers
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {blockers.length === 0 ? "None" : blockers.length}
              </Typography>
            </Box>
          </Box>

          {blockers.length > 0 && (
            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              sx={{ flexWrap: "wrap" }}
            >
              {blockers.slice(0, 3).map((blocker) => (
                <Chip
                  key={blocker}
                  label={blocker}
                  size="small"
                  variant="outlined"
                />
              ))}
              {blockers.length > 3 && (
                <Chip
                  label={`+${blockers.length - 3} more`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          )}
        </Stack>
      </CardActionArea>
    </Card>
  );
}

function CreateOrgDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [ownerUserId, setOwnerUserId] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [skipReview, setSkipReview] = useState(false);
  const [reconciliationPending, setReconciliationPending] = useState(false);
  const submitLock = useRef(false);

  const mutation = useMutation({
    mutationFn: (payload: Readonly<PlatformOrgCreate>) => createOrg(payload),
    onSuccess: async () => {
      setReconciliationPending(true);
      await onCreated();
      onClose();
    },
    onSettled: () => {
      submitLock.current = false;
    },
  });

  const canSubmit =
    ownerUserId.trim() !== "" &&
    businessName.trim() !== "" &&
    legalName.trim() !== "";
  const pending = mutation.isPending || reconciliationPending;

  const submit = () => {
    if (!canSubmit || pending || submitLock.current) return;
    const payload = Object.freeze({
      owner_user_id: ownerUserId.trim(),
      business_name: businessName.trim(),
      legal_name: legalName.trim(),
      skip_review: skipReview,
    }) satisfies Readonly<PlatformOrgCreate>;
    submitLock.current = true;
    mutation.mutate(payload);
  };

  const submittedSkipReview = mutation.variables?.skip_review ?? skipReview;
  const submitLabel = pending
    ? submittedSkipReview
      ? "Creating and activating..."
      : "Creating..."
    : skipReview
      ? "Create and activate"
      : "Create Organization";

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      aria-labelledby="create-organization-title"
      onClose={(_event, reason) => {
        if (
          pending &&
          (reason === "escapeKeyDown" || reason === "backdropClick")
        ) {
          return;
        }
        onClose();
      }}
    >
      <DialogTitle id="create-organization-title">
        Create Organization
      </DialogTitle>
      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {mutation.isError && (
              <Alert severity="error" role="alert">
                Could not create the organization. Verify the details and try
                again.
              </Alert>
            )}

            <TextField
              required
              fullWidth
              label="Owner User ID"
              placeholder="Firebase UID of the business owner"
              value={ownerUserId}
              disabled={pending}
              slotProps={{
                htmlInput: { "aria-label": "Owner User ID" },
              }}
              onChange={(event) => {
                if (!pending) setOwnerUserId(event.target.value);
              }}
            />
            <TextField
              required
              fullWidth
              label="Business Name"
              placeholder="Display name"
              value={businessName}
              disabled={pending}
              slotProps={{
                htmlInput: { "aria-label": "Business Name" },
              }}
              onChange={(event) => {
                if (!pending) setBusinessName(event.target.value);
              }}
            />
            <TextField
              required
              fullWidth
              label="Legal Name"
              placeholder="Legal entity name"
              value={legalName}
              disabled={pending}
              slotProps={{
                htmlInput: { "aria-label": "Legal Name" },
              }}
              onChange={(event) => {
                if (!pending) setLegalName(event.target.value);
              }}
            />

            <Paper variant="outlined" sx={{ p: 2 }}>
              <FormControlLabel
                disabled={pending}
                control={
                  <Checkbox
                    disabled={pending}
                    checked={skipReview}
                    onChange={(event) => {
                      if (!pending) setSkipReview(event.target.checked);
                    }}
                  />
                }
                label="Skip review"
                sx={{ m: 0 }}
              />
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{ mt: 0.75 }}
              >
                This bypasses the review queue and makes the organization active
                immediately.
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            type="button"
            variant="outlined"
            disabled={pending}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit || pending}
          >
            {submitLabel}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export function OnboardingPipelinePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const pipelineQuery = useQuery({
    queryKey: ["platform", "onboarding"],
    queryFn: listOnboardingOrgs,
  });

  const pipeline = pipelineQuery.data ?? {
    pending: [],
    in_review: [],
    approved: [],
  };
  const summary = useMemo(
    () => ({
      pending: pipeline.pending.length,
      inReview: pipeline.in_review.length,
      approved: pipeline.approved.length,
      blocked: [...pipeline.pending, ...pipeline.in_review].filter(
        (org) => (org.approval_blockers?.length ?? 0) > 0,
      ).length,
    }),
    [pipeline],
  );

  return (
    <PlatformPage
      title="Onboarding Pipeline"
      isLoading={pipelineQuery.isLoading}
      actions={
        <Button
          type="button"
          variant="contained"
          onClick={() => setShowCreateDialog(true)}
        >
          Create Organization
        </Button>
      }
    >
      {pipelineQuery.isError ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={pipelineQuery.isFetching}
              onClick={() => pipelineQuery.refetch()}
            >
              Retry
            </Button>
          }
        >
          The onboarding pipeline could not be loaded.
        </Alert>
      ) : (
        <Stack spacing={3}>
          <Box
            component="section"
            aria-label="Onboarding summary"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            <MetricCard label="Pending" value={summary.pending} />
            <MetricCard label="In review" value={summary.inReview} />
            <MetricCard label="Approved" value={summary.approved} />
            <MetricCard label="Blocked" value={summary.blocked} />
          </Box>

          <Box component="section" aria-labelledby="review-stage-heading">
            <Typography
              id="review-stage-heading"
              component="h2"
              variant="h5"
              sx={{ fontWeight: 700, mb: 2 }}
            >
              Review businesses by stage
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  xl: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              {QUEUES.map((queue) => {
                const organizations = pipeline[queue.key];
                const headingId = `onboarding-${queue.key}-heading`;

                return (
                  <Paper
                    key={queue.key}
                    component="section"
                    aria-labelledby={headingId}
                    variant="outlined"
                    sx={{
                      borderTop: 3,
                      borderTopColor: queue.accent,
                      p: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        id={headingId}
                        component="h3"
                        variant="h6"
                        sx={{ fontWeight: 700 }}
                      >
                        {queue.label}
                      </Typography>
                      <Chip
                        label={organizations.length.toLocaleString()}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>

                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      {organizations.length === 0 ? (
                        <Paper
                          variant="outlined"
                          sx={{
                            borderStyle: "dashed",
                            p: 3,
                            textAlign: "center",
                          }}
                        >
                          <Typography color="text.secondary" variant="body2">
                            No organizations in this queue.
                          </Typography>
                        </Paper>
                      ) : (
                        organizations.map((org) => (
                          <OrgCard
                            key={org.org_id}
                            org={org}
                            onOpen={() =>
                              navigate(`/organizations/${org.org_id}`)
                            }
                          />
                        ))
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Box>
          </Box>
        </Stack>
      )}

      {showCreateDialog && (
        <CreateOrgDialog
          onClose={() => setShowCreateDialog(false)}
          onCreated={async () => {
            await Promise.allSettled([
              queryClient.invalidateQueries({
                queryKey: ["platform", "onboarding"],
              }),
              queryClient.invalidateQueries({
                queryKey: ["platform", "orgs"],
              }),
            ]);
          }}
        />
      )}
    </PlatformPage>
  );
}
